import { SlideObject, defaultFontWeight } from '../types';
import { hasEditableText } from './shapeFrame';
import { inlineTypographyKeysFromPatch, sanitizePresentationHtml, stripInlineCssProps } from './textSanitize';
import { applyListHtml, ListKind } from './listHtml';

export const TEXT_STYLE_PATCH_KEYS = [
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'textDecoration',
  'color',
  'textAlign',
  'textTransform',
  'verticalAlign',
  'lineHeight',
  'letterSpacing',
  'padding',
] as const;

const TABLE_TEXT_STYLE_PATCH_KEYS = [
  'fontFamily',
  'fontSize',
  'fontWeight',
  'color',
  'textAlign',
  'textTransform',
  'verticalAlign',
  'lineHeight',
  'letterSpacing',
] as const;

export function isTextStylePatch(patch: Record<string, unknown>): boolean {
  const keys = Object.keys(patch).filter((key) => patch[key] != null);
  return keys.length > 0 && keys.every((key) => (TEXT_STYLE_PATCH_KEYS as readonly string[]).includes(key));
}

function isTableTextStylePatch(patch: Record<string, unknown>): boolean {
  const keys = Object.keys(patch).filter((key) => patch[key] != null);
  return keys.length > 0 && keys.every((key) => (TABLE_TEXT_STYLE_PATCH_KEYS as readonly string[]).includes(key));
}

export function selectedTextObjects(objects: SlideObject[], selectedIds: string[]): SlideObject[] {
  const ids = new Set(selectedIds);
  return objects.filter((object) => ids.has(object.id) && hasEditableText(object));
}

export function preferredToolForSelection(objects: SlideObject[], ids: string[]): 'text' | 'image' | 'table' | 'chart' | null {
  const selected = ids
    .map((id) => objects.find((object) => object.id === id))
    .filter((object): object is SlideObject => Boolean(object));
  if (selected.some((object) => hasEditableText(object))) return 'text';
  if (selected.some((object) => object.type === 'table')) return 'table';
  if (selected.some((object) => object.type === 'chart')) return 'chart';
  if (selected.some((object) => object.type === 'image' || object.type === 'product')) return 'image';
  return null;
}

export function styleTargetIds(
  objects: SlideObject[],
  selectedIds: string[],
  patch: Record<string, unknown>,
): string[] {
  if (isTextStylePatch(patch)) {
    const ids = new Set(selectedIds);
    const supportsTable = isTableTextStylePatch(patch);
    return objects
      .filter((object) => ids.has(object.id) && (hasEditableText(object) || (supportsTable && object.type === 'table')))
      .map((object) => object.id);
  }
  return selectedIds.filter((id) => objects.some((object) => object.id === id));
}

export function applyStylePatchToSelection(
  objects: SlideObject[],
  selectedIds: string[],
  patch: Record<string, unknown>,
): SlideObject[] {
  const targets = new Set(styleTargetIds(objects, selectedIds, patch));
  if (!targets.size) return objects;
  const typographyKeys = inlineTypographyKeysFromPatch(patch);
  return objects.map((object) => {
    if (!targets.has(object.id)) return object;
    const nextStyle = { ...object.style, ...patch };
    if (!hasEditableText(object) || !typographyKeys.length) {
      return { ...object, style: nextStyle };
    }
    return {
      ...object,
      style: nextStyle,
      content: sanitizePresentationHtml(stripInlineCssProps(object.content, typographyKeys)),
    };
  });
}

export function applyTextStyleStepToSelection(
  objects: SlideObject[],
  selectedIds: string[],
  patch: Record<string, unknown>,
  list?: ListKind,
): SlideObject[] {
  const targets = new Set(selectedTextObjects(objects, selectedIds).map((object) => object.id));
  if (!targets.size) return objects;
  const typographyKeys = inlineTypographyKeysFromPatch(patch);
  return objects.map((object) => {
    if (!targets.has(object.id)) return object;
    let content = object.content || '';
    if (typographyKeys.length) content = sanitizePresentationHtml(stripInlineCssProps(content, typographyKeys));
    if (list) content = applyListHtml(content, list);
    return { ...object, style: { ...object.style, ...patch }, content };
  });
}

export function applyBoxMarkToSelection(
  objects: SlideObject[],
  selectedIds: string[],
  mark: 'bold' | 'italic' | 'underline',
  primaryId: string,
): SlideObject[] {
  const primary = objects.find((object) => object.id === primaryId);
  const targets = new Set(selectedTextObjects(objects, selectedIds).map((object) => object.id));
  if (!primary || !targets.size) return objects;
  const turnOn = mark === 'bold'
    ? Number(primary.style?.fontWeight || 0) !== 700
    : mark === 'italic'
      ? primary.style?.fontStyle !== 'italic'
      : primary.style?.textDecoration !== 'underline';
  return objects.map((object) => {
    if (!targets.has(object.id)) return object;
    const style = { ...(object.style || {}) };
    if (mark === 'bold') {
      style.fontWeight = turnOn ? 700 : defaultFontWeight(style.fontFamily);
      return {
        ...object,
        style,
        content: sanitizePresentationHtml(stripInlineCssProps(object.content, ['fontWeight'])),
      };
    }
    if (mark === 'italic') style.fontStyle = turnOn ? 'italic' : 'normal';
    else style.textDecoration = turnOn ? 'underline' : 'none';
    return { ...object, style };
  });
}

export function querySharedTextStyle(
  objects: SlideObject[],
  selectedIds: string[],
  key: 'fontFamily' | 'fontSize' | 'color' | 'fontWeight',
): { value: string | number | null; mixed: boolean } {
  const texts = selectedTextObjects(objects, selectedIds);
  if (!texts.length) return { value: null, mixed: false };
  const values = texts.map((object) => {
    const style = object.style || {};
    if (key === 'fontFamily') return style.fontFamily || null;
    if (key === 'fontSize') return style.fontSize ?? null;
    if (key === 'color') return style.color || null;
    return style.fontWeight ?? defaultFontWeight(style.fontFamily);
  });
  const unique = new Set(values.map((value) => (value == null ? '' : String(value))));
  if (unique.size > 1) return { value: null, mixed: true };
  return { value: values[0] ?? null, mixed: false };
}
